import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, ListChecks } from "lucide-react";
import { PlanStatusBadge } from "@/components/ai/PlanStatusBadge";
import { PlanChangeSummaryList } from "@/components/ai/PlanChangeSummaryList";
import { ActionPlanRecommendationItem } from "@/components/ai/ActionPlanRecommendationItem";
import { DraftMessagePreview } from "@/components/ai/DraftMessagePreview";
import { ActionPlanReviewDialog } from "@/components/ai/ActionPlanReviewDialog";
import { ActionPlanConfirmDialog } from "@/components/ai/ActionPlanConfirmDialog";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

/**
 * v1.3 Wave 2 — the compact Action Plan card rendered inline in the AI
 * chat thread. This is the ONLY thing a "Approve & Execute" click can
 * ever do directly: open the hard-stop confirmation dialog. Nothing on
 * this card calls the approve endpoint itself — that only ever happens
 * from inside `ActionPlanConfirmDialog`'s explicit confirm button, per
 * the product's two-phase safety model (Draft -> Human Review ->
 * Approve & Execute).
 */
export const ActionPlanMessageCard = ({ plan, onApprove, onReject, isActing }) => {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const showTier = isDemoUser(user);

  const status = plan.status;
  const isProposed = status === "proposed";
  const isExecuting = status === "executing";
  const recommendations = plan.recommendations || [];
  const draftMessages = plan.draft_messages || [];

  const openConfirmFromReview = () => {
    setReviewOpen(false);
    setConfirmOpen(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border bg-card"
      data-testid="ai-action-plan-card"
      data-plan-id={plan.id}
      data-plan-status={status}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-[hsl(var(--ai-plan))]" />
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <h4 className="font-heading text-base font-semibold leading-6 tracking-tight flex items-center gap-1.5 flex-wrap" data-testid="ai-action-plan-title">
            {plan.title}
            {showTier && <TierBadge tier="elite" size="xs" />}
          </h4>
          <PlanStatusBadge status={status} />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="ai-action-plan-summary">
          {plan.summary}
        </p>

        {Object.keys(plan.metrics || {}).length > 0 && (
          <div className="mt-3">
            <PlanChangeSummaryList counts={plan.metrics} testIdPrefix="ai-action-plan-metric" />
          </div>
        )}

        {recommendations.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-semibold text-foreground">Recommendations</p>
            {recommendations.slice(0, 3).map((rec, idx) => (
              <ActionPlanRecommendationItem key={rec.appointment_id || idx} recommendation={rec} />
            ))}
            {recommendations.length > 3 && (
              <p className="mt-1 text-[11px] text-muted-foreground">+{recommendations.length - 3} more in Review Plan</p>
            )}
          </div>
        )}

        {draftMessages.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-semibold text-foreground">Draft message</p>
            <DraftMessagePreview message={draftMessages[0]} />
            {draftMessages.length > 1 && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 mt-1 text-xs"
                onClick={() => setReviewOpen(true)}
                data-testid="ai-action-plan-view-drafts-button"
              >
                View all {draftMessages.length} drafts
              </Button>
            )}
          </div>
        )}

        {status === "failed" && plan.error && (
          <p className="mt-3 text-xs text-destructive" data-testid="ai-action-plan-error">
            {plan.error}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReviewOpen(true)}
            data-testid="ai-action-plan-review-button"
          >
            <ListChecks className="h-3.5 w-3.5" /> Review Plan
          </Button>
          <div className="flex items-center gap-2">
            {isProposed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReject(plan.id)}
                disabled={isActing}
                data-testid="ai-action-plan-reject-button"
              >
                Reject
              </Button>
            )}
            {(isProposed || isExecuting) && (
              <Button
                size="sm"
                className="bg-[hsl(var(--role-accent))] hover:opacity-95"
                onClick={() => setConfirmOpen(true)}
                disabled={!isProposed || isActing}
                data-testid="ai-action-plan-approve-execute-button"
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Executing&hellip;
                  </>
                ) : (
                  "Approve & Execute"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ActionPlanReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        plan={plan}
        onApproveClick={openConfirmFromReview}
        canAct={isProposed}
      />
      <ActionPlanConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        plan={plan}
        loading={isActing}
        onConfirm={() => {
          setConfirmOpen(false);
          onApprove(plan.id);
        }}
      />
    </div>
  );
};
